/**
 * Saved OFAC dashboards for one subscription account.
 *
 * Practice this follows: show decisions before trends, let the account hide
 * widgets it does not use, and keep each card one click from the tool that
 * does the work. Color is a hint; the status sentence is the meaning.
 */

export type OfacDashboardWidgetId =
  | 'matches-waiting'
  | 'review-clock'
  | 'list-freshness'
  | 'treasury-notices'
  | 'names-screened'
  | 'capta-lists'
  | 'general-licenses'
  | 'investigations'
  | 'whitelist';

export type OfacDashboardNavTarget =
  | 'ofacScreening'
  | 'ofacLiveFeed'
  | 'ofacCapta'
  | 'ofacInvestigations'
  | 'ofacWhitelist'
  | 'ofacCompliance';

export type OfacDashboardWidget = {
  id: OfacDashboardWidgetId;
  nav: OfacDashboardNavTarget;
  /** On for a new dashboard. The account can turn any of these off. */
  defaultOn: boolean;
  title: { en: string; es: string };
  blurb: { en: string; es: string };
};

export const OFAC_DASHBOARD_WIDGETS: readonly OfacDashboardWidget[] = [
  {
    id: 'matches-waiting',
    nav: 'ofacScreening',
    defaultOn: true,
    title: { en: 'Names that need a decision', es: 'Nombres que necesitan una decisión' },
    blurb: {
      en: 'Possible matches still waiting. Open Screening to clear or escalate each one.',
      es: 'Posibles coincidencias que siguen en espera. Abra Screening para descartar o escalar cada una.',
    },
  },
  {
    id: 'review-clock',
    nav: 'ofacScreening',
    defaultOn: true,
    title: { en: 'Review clock', es: 'Plazo de revisión' },
    blurb: {
      en: 'How many open reviews are past the plan’s business-day clock.',
      es: 'Cuántas revisiones abiertas pasaron el plazo en días hábiles del plan.',
    },
  },
  {
    id: 'list-freshness',
    nav: 'ofacLiveFeed',
    defaultOn: true,
    title: { en: 'Are the lists current?', es: '¿Las listas están al día?' },
    blurb: {
      en: 'SDN and CAPTA refresh status. Screening waits on a current list.',
      es: 'Estado de actualización de SDN y CAPTA. El cribado espera una lista vigente.',
    },
  },
  {
    id: 'treasury-notices',
    nav: 'ofacLiveFeed',
    defaultOn: true,
    title: { en: 'Treasury notices', es: 'Avisos del Tesoro' },
    blurb: {
      en: 'Recent OFAC email notices that refresh lists and general licenses.',
      es: 'Avisos recientes de OFAC por correo que actualizan listas y licencias generales.',
    },
  },
  {
    id: 'names-screened',
    nav: 'ofacScreening',
    defaultOn: true,
    title: { en: 'Names screened', es: 'Nombres cribados' },
    blurb: {
      en: 'How many names this account screened in the last 30 days.',
      es: 'Cuántos nombres cribó esta cuenta en los últimos 30 días.',
    },
  },
  {
    id: 'capta-lists',
    nav: 'ofacCapta',
    defaultOn: false,
    title: { en: 'CAPTA lists', es: 'Listas CAPTA' },
    blurb: {
      en: 'Correspondent-account restrictions. Separate from the SDN blocking list.',
      es: 'Restricciones de cuentas corresponsales. Distinto de la lista de bloqueo SDN.',
    },
  },
  {
    id: 'general-licenses',
    nav: 'ofacLiveFeed',
    defaultOn: false,
    title: { en: 'General licenses', es: 'Licencias generales' },
    blurb: {
      en: 'Official OFAC general licenses on file. A license does not skip a name check.',
      es: 'Licencias generales oficiales de OFAC en el archivo. Una licencia no omite el cribado de un nombre.',
    },
  },
  {
    id: 'investigations',
    nav: 'ofacInvestigations',
    defaultOn: false,
    title: { en: 'Investigations', es: 'Investigaciones' },
    blurb: {
      en: 'Escalated matches, including the 50% rule for owners.',
      es: 'Coincidencias escaladas, incluida la regla del 50% para propietarios.',
    },
  },
  {
    id: 'whitelist',
    nav: 'ofacWhitelist',
    defaultOn: false,
    title: { en: 'Confirmed false positives', es: 'Falsos positivos confirmados' },
    blurb: {
      en: 'Names already reviewed and recorded as not a match. The next screen still runs.',
      es: 'Nombres ya revisados y registrados como no coincidencia. El siguiente cribado igual se ejecuta.',
    },
  },
];

export type OfacDashboardRecord = {
  id: string;
  accountId: string;
  name: string;
  widgetIds: OfacDashboardWidgetId[];
  updatedAt: string;
};

export type OfacDashboardLive = {
  queueCount: number;
  screened30d: number;
  openClocks: number;
  overdueClocks: number;
  noticeCount: number;
  planLabel: string;
  lists: Array<{ name: string; freshness: string; status: string }>;
  clearCount?: number;
  reviewCount?: number;
  hitCount?: number;
  volumePoints?: number[];
  exceptionPoints?: number[];
};

export const EMPTY_OFAC_DASHBOARD_LIVE: OfacDashboardLive = {
  queueCount: 0,
  screened30d: 0,
  openClocks: 0,
  overdueClocks: 0,
  noticeCount: 0,
  planLabel: '',
  lists: [],
};

const WIDGET_INDEX = new Map(OFAC_DASHBOARD_WIDGETS.map((widget) => [widget.id, widget]));

export const ofacDashboardWidget = (id: string): OfacDashboardWidget | undefined => WIDGET_INDEX.get(id as OfacDashboardWidgetId);

export const defaultOfacDashboardWidgets = (): OfacDashboardWidgetId[] => (
  OFAC_DASHBOARD_WIDGETS.filter((widget) => widget.defaultOn).map((widget) => widget.id)
);

export const sanitizeOfacWidgetIds = (ids: readonly string[]): OfacDashboardWidgetId[] => {
  const seen = new Set<OfacDashboardWidgetId>();
  ids.forEach((id) => {
    if (WIDGET_INDEX.has(id as OfacDashboardWidgetId)) {
      seen.add(id as OfacDashboardWidgetId);
    }
  });
  return OFAC_DASHBOARD_WIDGETS.map((widget) => widget.id).filter((id) => seen.has(id));
};

export const createOfacDashboard = (
  accountId: string,
  name: string,
  now = new Date().toISOString(),
): OfacDashboardRecord => ({
  id: `ofac-dash-${now.replace(/[^0-9]/g, '').slice(0, 14)}-${Math.random().toString(36).slice(2, 8)}`,
  accountId: accountId.trim() || 'subscription',
  name: name.trim() || 'Sanctions dashboard',
  widgetIds: defaultOfacDashboardWidgets(),
  updatedAt: now,
});

export const toggleOfacDashboardWidget = (
  dashboard: OfacDashboardRecord,
  widgetId: OfacDashboardWidgetId,
  included: boolean,
  now = new Date().toISOString(),
): OfacDashboardRecord => {
  const current = new Set(dashboard.widgetIds);
  if (included) {
    current.add(widgetId);
  } else {
    current.delete(widgetId);
  }
  return {
    ...dashboard,
    widgetIds: sanitizeOfacWidgetIds([...current]),
    updatedAt: now,
  };
};

export const dashboardsForAccount = (
  dashboards: readonly OfacDashboardRecord[],
  accountId: string,
): OfacDashboardRecord[] => dashboards.filter((row) => row.accountId === accountId);

export type OfacDashboardWidgetView = {
  id: OfacDashboardWidgetId;
  nav: OfacDashboardNavTarget;
  title: string;
  blurb: string;
  status: string;
  tone: 'clear' | 'watch' | 'act';
};

export const ofacDashboardWidgetView = (
  widgetId: OfacDashboardWidgetId,
  live: OfacDashboardLive,
  language: 'en' | 'es',
): OfacDashboardWidgetView => {
  const widget = WIDGET_INDEX.get(widgetId)!;
  const es = language === 'es';
  let status = es ? 'Abra la herramienta para ver el detalle.' : 'Open the tool for the detail.';
  let tone: OfacDashboardWidgetView['tone'] = 'clear';
  if (widgetId === 'matches-waiting') {
    if (live.queueCount > 0) {
      status = es
        ? `${live.queueCount} nombre${live.queueCount === 1 ? '' : 's'} espera${live.queueCount === 1 ? '' : 'n'} una decisión`
        : `${live.queueCount} name${live.queueCount === 1 ? '' : 's'} still need a decision`;
      tone = 'act';
    } else {
      status = es ? 'Ningún nombre está en espera' : 'No names are waiting';
    }
  } else if (widgetId === 'review-clock') {
    if (live.overdueClocks > 0) {
      status = es
        ? `${live.overdueClocks} revisión${live.overdueClocks === 1 ? '' : 'es'} pasó el plazo`
        : `${live.overdueClocks} review${live.overdueClocks === 1 ? '' : 's'} past the clock`;
      tone = 'act';
    } else if (live.openClocks > 0) {
      status = es
        ? `${live.openClocks} revisión${live.openClocks === 1 ? '' : 'es'} abierta${live.openClocks === 1 ? '' : 's'}, dentro del plazo`
        : `${live.openClocks} open review${live.openClocks === 1 ? '' : 's'}, still inside the clock`;
      tone = 'watch';
    } else {
      status = es ? 'Ningún plazo de revisión está abierto' : 'No review clock is open';
    }
  } else if (widgetId === 'list-freshness') {
    if (!live.lists.length) {
      status = es ? 'Aún no hay fecha de actualización' : 'No refresh date yet';
      tone = 'watch';
    } else {
      const stale = live.lists.filter((row) => /stale|error|fail|old/i.test(`${row.status} ${row.freshness}`));
      status = live.lists.map((row) => `${row.name}: ${row.freshness || row.status}`).join(' · ');
      tone = stale.length ? 'watch' : 'clear';
    }
  } else if (widgetId === 'treasury-notices') {
    status = live.noticeCount
      ? (es ? `${live.noticeCount} aviso${live.noticeCount === 1 ? '' : 's'} reciente${live.noticeCount === 1 ? '' : 's'}` : `${live.noticeCount} recent notice${live.noticeCount === 1 ? '' : 's'}`)
      : (es ? 'Ningún aviso reciente' : 'No recent notice');
  } else if (widgetId === 'names-screened') {
    status = es
      ? `${live.screened30d} en los últimos 30 días`
      : `${live.screened30d} in the last 30 days`;
  } else if (widgetId === 'capta-lists') {
    status = es ? 'Abra las listas CAPTA para ver cada banco' : 'Open CAPTA lists to see each bank';
  } else if (widgetId === 'general-licenses') {
    status = es ? 'Abra el feed para ver las licencias en archivo' : 'Open the feed to see licenses on file';
  } else if (widgetId === 'investigations') {
    status = es ? 'Abra Investigaciones para los casos escalados' : 'Open Investigations for escalated cases';
  } else if (widgetId === 'whitelist') {
    status = es ? 'Abra la lista de falsos positivos confirmados' : 'Open the confirmed false-positive list';
  }
  if (live.planLabel && widgetId === 'names-screened') {
    status = `${status} · ${live.planLabel}`;
  }
  return {
    id: widgetId,
    nav: widget.nav,
    title: widget.title[language],
    blurb: widget.blurb[language],
    status,
    tone,
  };
};

export const OFAC_DASHBOARDS_STORAGE_KEY = 'nextcompli.ofac-dashboards.v1';

export const loadOfacDashboards = (storage: Pick<Storage, 'getItem'> | null): OfacDashboardRecord[] => {
  if (!storage) {
    return [];
  }
  try {
    const raw = storage.getItem(OFAC_DASHBOARDS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.flatMap((row) => {
      if (!row || typeof row !== 'object') {
        return [];
      }
      const record = row as Partial<OfacDashboardRecord>;
      const accountId = String(record.accountId || '').trim();
      const name = String(record.name || '').trim();
      if (!accountId || !name) {
        return [];
      }
      return [{
        id: String(record.id || createOfacDashboard(accountId, name).id),
        accountId,
        name,
        widgetIds: sanitizeOfacWidgetIds(Array.isArray(record.widgetIds) ? record.widgetIds : []),
        updatedAt: String(record.updatedAt || ''),
      }];
    });
  } catch {
    return [];
  }
};

export const saveOfacDashboards = (
  storage: Pick<Storage, 'setItem'> | null,
  dashboards: readonly OfacDashboardRecord[],
): void => {
  if (!storage) {
    return;
  }
  storage.setItem(OFAC_DASHBOARDS_STORAGE_KEY, JSON.stringify(dashboards));
};
